/**
 * PublicReviewsStrip — iter1114
 *
 * Fetches approved public reviews from /api/public/reviews and renders
 * them below the static hero testimonials on the Landing page. Hidden
 * entirely when there are no approved reviews yet (fail-closed).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";

export default function PublicReviewsStrip({ isDark, bg = true }) {
  const [items, setItems] = useState(null);
  // public endpoint — no auth required; use REACT_APP_BACKEND_URL directly.
  const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  useEffect(() => {
    axios
      .get(`${API}/public/reviews`, { params: { limit: 9 } })
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => setItems([]));
  }, [API]);

  if (items === null) return null;      // loading
  if (items.length === 0) return null;  // no approved reviews yet → hide

  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 mb-12"
      data-testid="public-reviews-strip"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className={`text-lg sm:text-xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
          {bg ? "Какво казват нашите потребители" : "What our users say"}
        </h2>
        <span className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {bg
            ? `${items.length} проверени отзива — одобрени от екипа`
            : `${items.length} verified reviews · approved by our team`}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((r) => (
          <div
            key={r.id}
            data-testid={`public-review-${r.id}`}
            className={`relative rounded-xl p-4 border ${
              isDark
                ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]"
                : "bg-white border-slate-100 shadow-sm"
            }`}
          >
            {r.rating && (
              <div className="flex items-center gap-0.5 mb-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`w-3.5 h-3.5 ${
                      s <= r.rating
                        ? "text-amber-500 fill-amber-500"
                        : isDark
                        ? "text-slate-700"
                        : "text-slate-200"
                    }`}
                  />
                ))}
              </div>
            )}
            {r.text && (
              <p className={`text-xs leading-snug ${isDark ? "text-slate-300" : "text-[#1A1A2E]"}`}>
                &ldquo;{r.text}&rdquo;
              </p>
            )}
            <div className="mt-2 text-[11px] font-semibold" style={{ color: "var(--brand-green-solid)" }}>
              {r.name}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
